import { useEffect, useState } from 'react';
import { formatAppVersion, readAppVersion } from '@/lib/appVersion';
import { forceRefresh, subscribeUpdate } from '@/lib/appUpdate';
import { Link } from 'react-router-dom';
import { Card, EmptyState } from '@/components/ui';
import { useMe } from './useAuth';
import { ChangelogModal } from './ChangelogModal';

/**
 * 메뉴 카드에 곁들이는 한 줄 설명.
 *
 * 원래 이 자리에 라우트 경로(/trading 같은 것)를 찍었는데, 쓰는 사람에게
 * 아무 의미가 없다. 주소는 눌러서 가면 주소창에 나온다.
 *
 * 메뉴 목록 자체는 서버가 준 것만 그린다 — 여기 있는 것은 설명 문구뿐이다.
 * 그래서 이 표에 없는 코드가 와도 카드는 정상적으로 그려진다. 설명만 비는 것이,
 * 새 메뉴가 생겼을 때 홈이 깨지는 것보다 낫다.
 */
const MENU_DESCRIPTION: Record<string, string> = {
  TRADING: '종목 · 추천 · AI 모의투자(저점매수 · 스윙 · 미국)',
  TRADING_ACCOUNT: '보유 · 체결 · 주문 가능 현금 · 조건주문 · 손익 곡선 · 매매일지 · 자동매매',
  LEDGER: '수입·지출과 월별 집계',
  BENEFIT: '카드·통신 혜택 알림',
  ADMIN: '사용자와 권한, 배치 · 비용 · 모의투자 회차와 기업행위 확인',
};

/**
 * 로그인 직후의 홈.
 *
 * 접근 가능한 메뉴를 카드로 보여준다.
 * 첫 메뉴로 자동 이동시키지 않는 이유
 *   사람마다 볼 수 있는 메뉴가 다르다. 자동 이동하면 주소창의 / 와
 *   실제 화면이 어긋나고, 뒤로가기가 이상하게 동작한다.
 *
 * Phase 3 에서 자산 요약 홈으로 바뀐다. (00-architecture.md 7절)
 */
export function HomePage() {
  const { me } = useMe();

  if (!me) {
    return null;
  }

  if (me.menus.length === 0) {
    return (
      <EmptyState
        title="접근 가능한 화면이 없습니다."
        description="관리자에게 권한을 요청하세요."
      />
    );
  }

  return (
    <>
      <div className="grid gap-3 sm:grid-cols-2">
        {me.menus.map((menu) => (
          <Link key={menu.code} to={menu.path} className="block">
            <Card className="transition-colors hover:border-line-strong">
              <p className="text-sm font-medium text-fg">{menu.name}</p>
              {MENU_DESCRIPTION[menu.code] && (
                <p className="mt-1 text-xs text-fg-muted">{MENU_DESCRIPTION[menu.code]}</p>
              )}
            </Card>
          </Link>
        ))}
      </div>

      <VersionLine />
    </>
  );
}

/**
 * 버전 한 줄. 홈 화면 맨 아래 가운데.
 *
 * 왜 우측 하단 고정이 아닌가
 *   폰에서 하단 탭바와 겹친다. 고정 요소를 하나 더 두면 화면 세로가 그만큼 줄고,
 *   본문 마지막 줄이 가려지지 않게 또 여백을 계산해야 한다.
 *   (AppLayout 의 pb-16 이 이미 그 문제를 풀고 있다)
 *
 *   흐르는 위치에 두면 스크롤을 끝까지 내렸을 때 자연스럽게 나온다.
 *   일반적인 사이트의 푸터가 있는 자리이기도 하다.
 *
 * 왜 홈 화면에만 두나
 *   모든 화면에 붙이면 표와 차트 아래마다 회색 글씨가 하나씩 더 생긴다.
 *   버전을 확인하려는 사람은 홈으로 돌아올 수 있다 —
 *   로고를 누르면 홈이라는 것은 이미 만들어 뒀다.
 *
 * 🔴 값이 없으면 아무것도 그리지 않는다
 *   로컬 개발에서는 언제나 없다. 그때 "개발" 같은 가짜를 띄우면
 *   화면에 뜬 버전이 진짜인지 매번 확인해야 한다. (lib/appVersion.ts)
 */
function VersionLine() {
  const version = readAppVersion(import.meta.env);
  const [open, setOpen] = useState(false);
  /** 서버가 알려준 새 버전. 지금 화면과 어긋날 때만 값이 들어온다 */
  const [pendingVersion, setPendingVersion] = useState<string | null>(null);

  useEffect(
    () => subscribeUpdate((state) => setPendingVersion(state.available ? state.version : null)),
    [],
  );

  if (version === null) {
    return null;
  }

  /*
    버전을 누르면 업데이트 내역이 열린다. (2026-08-19)

    🔴 버전 숫자만 있으면 "무엇이 바뀌었나" 를 볼 곳이 없다
      배포는 하루에도 몇 번 일어나는데 사용자는 화면이 달라진 것만 본다.
      숫자 옆에 링크를 따로 두지 않고 숫자 자체를 누르게 한 것은,
      그 자리가 이미 "이 앱의 지금 상태" 를 뜻하기 때문이다.
  */
  return (
    <>
      <p className="mt-6 text-center text-xs text-fg-subtle">
        파크302호 개미들 ·{" "}
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="underline decoration-dotted underline-offset-2 hover:text-fg-muted"
        >
          {formatAppVersion(version)}
        </button>
      </p>

      {/*
        🔴 어긋났을 때만 뜬다 (2026-08-30 사용자 피드백으로 변경)

          그전에는 늘 떠 있었다. 89번에서 이 버튼을 만들 때는 version.json 이
          없어 "지금이 최신인가" 를 알 방법 자체가 없었기 때문이다.
          이제는 대조할 수 있으므로, 최신인데도 업데이트를 권하는 것은
          사용자에게 "뭔가 잘못됐나" 하는 인상만 준다.

          평소의 탈출구는 업데이트 내역 모달 안으로 옮겼다
          (거기 "최신으로 다시 받기").
      */}
      {pendingVersion !== null && (
        <p className="mt-2 text-center">
          <button
            type="button"
            onClick={() => void forceRefresh()}
            className={
              'rounded-lg bg-brand-weak px-3 py-1.5 text-xs font-medium text-brand ' +
              'transition-colors hover:bg-brand hover:text-brand-fg'
            }
          >
            {pendingVersion} 버전으로 업데이트
          </button>
        </p>
      )}

      <ChangelogModal
        open={open}
        onClose={() => setOpen(false)}
        currentVersion={version.version}
      />
    </>
  );
}
